import { CgpConfirmationDialogService } from './../../../../../shared/cgp-confirmation-dialog/cgp-confirmation-dialog.service';
import { DataTableComponent } from './../../../../../shared/data-table/data-table.component';
import { CgpConfirmationDialogComponent } from 'src/app/shared/cgp-confirmation-dialog/cgp-confirmation-dialog.component';
import { finalize, filter, flatMap, debounceTime, take } from 'rxjs/operators';
import { Component, Inject, OnInit, ViewChild, EventEmitter } from '@angular/core';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { TprNecApiService } from './tpr-nec-modal.service';
import { LazyLoadEvent } from 'primeng/api/public_api';
import { ColumnDef } from '../../../../../shared/data-table/data-table.interface';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { EcmService } from '../../../ecm/ecm.service';
import { BehaviorSubject, pipe } from 'rxjs';
import { TprNecCompleteDateComponent } from './tpr-nec-complete-date/tpr-nec-complete-date.component';
import { TprecmAuthorizationService } from '../../../authorization/tprecm-authorization.service';

interface TprNecTool {
   fixtureNo: string;
   programName: string;
   selectedToolId: number;
   selectedTprToolId: number;
}

/**
 * Data provided by a given row in the table
 *
 * @interface TprNecReportsRowData
 */
interface TprNecReportsRowData {
   fixtureNo: number;
   necLec: string;
   necCompleteDate: string;
}

@Component({
   selector: 'tpr-nec-modal',
   templateUrl: './tpr-nec-modal.template.html',
   styleUrls: ['../../../tprecm.style.scss', './tpr-nec-modal.scss']
})
export class TprNecModalComponent implements OnInit {
   @ViewChild('tprGrid') private readonly grid: DataTableComponent;

   fixture: TprNecTool;
   readonly: boolean;
   selectedNec;
   necs: any[] = [];
   loading = false;
   hasLoaded = false;
   resourced = false;

   necData$: BehaviorSubject<any[]> = new BehaviorSubject([]);
   refresh$: EventEmitter<void> = new EventEmitter();


   /**
    * Column names and values for the tprNec table.
    * Matches the DTO.
    *
    * @type {any[]}
    * @memberof TprNecModalComponent
    */
   columns: ColumnDef[] = [
      { field: 'necLec', name: 'NEC LEC', sortingDisabled: true, editable: false },
      { field: 'necCompleteDate', name: 'NEC Compl Date', type: 'date', editable: false, tooltip: 'Notification Engineering Change Complete Date' },
      {
         field: 'editDate', name: 'Edit Nec Complete Date', type: 'icon-button', tooltip: 'Click this button to edit the Nec Complete Date.',
         icon: 'create', sortingDisabled: true, width: '12em',
         callback: this.editNecCompleteDate.bind(this)
      }
   ];
   selectedNecLec: any;

   constructor(
      private readonly router: Router,
      public readonly dialog: MatDialogRef<TprNecModalComponent>,
      private readonly dialogRef: MatDialog,
      private readonly toastr: ToastrService,
      private readonly ecmService: EcmService,
      private readonly tprNecService: TprNecApiService,
      private readonly confirmation: CgpConfirmationDialogService,
      private readonly auth: TprecmAuthorizationService,
      @Inject(MAT_DIALOG_DATA) public readonly data: any
   ) { }

   ngOnInit() {
      if (this.router.url === '/tprecm/view-update-tpr/re-source') {
         this.resourced = true;
         this.columns.forEach(val => { val.editable = false; val.headerHighlight = false; });
         this.columns.forEach(val => {
            if (val.field === 'necLec') {
               return (val.type = 'readonly');
            }
         });
      }
      else {
         this.auth.getAuthorizationByFeaturePermission('ecm', 'nec-edit')
            .then(editable => this.columns.find(col => col.field === 'editDate').editable = editable);
      }


      if (!this.validateDialogData()) {
         this.dialog.close();
         return;
      }

      // Setup local values
      this.fixture = this.data;
      this.readonly = this.data.readonly;


      // Update NEC Data
      this.refresh$.pipe(
         debounceTime(1000)
      ).subscribe(this.loadNec.bind(this));

      this.refresh$.emit();

      if (this.readonly) {
         this.columns.forEach(col => col.editable = false);
      }

      this.hasLoaded = true;
   }

   /** CLASS FUNCTIONS */

   /**
    * Validate dialogs incoming data
    */
   validateDialogData(): boolean {
      return this.data.fixtureNo != null;
   }

   loadNec(necLec?: any) {
      this.loading = true;
      this.tprNecService.getTprNec(this.data.selectedToolId)
         .pipe(finalize(() => this.loading = false))
         .subscribe(data => {
            this.necData$.next(data instanceof Array ? data : []);
            if (necLec) {
               this.grid.selections = [this.necData$.value.find(el => el.necLec === necLec)];
               this.selectedNecLec = this.grid.selections[0];
            }
         });
   }

   /**
 * Open a dialog to edit a given row NEC Complete Date
 *
 * @param {TprNecReportsRowData} rowData
 * @memberof TprNecModalComponent
 */
   editNecCompleteDate(rowData: TprNecReportsRowData): void {
      const fixtureNo = this.data.fixtureNo;

      const dialogRef = this.dialogRef.open(TprNecCompleteDateComponent, {

         data: { ...rowData, fixtureNo, selectedToolId: this.data.selectedToolId, selectedTprToolId: this.data.selectedTprToolId, programName: this.data.programName }
      });

      dialogRef.afterClosed()
         .pipe(
            filter(Boolean),
            flatMap(() => this.printReport(rowData, fixtureNo, 'Update was successful!  Do you want to save this NEC file to the NEC folder?').afterClosed())
         ).subscribe(this.refresh.bind(this, rowData.necLec));
   }

   printReport(data: TprNecReportsRowData, fixtureNo: string, actionString: string): any {
      const reportTag = data.necCompleteDate ? 'NEC' : 'IN_PROCESS';
      this.auth.userRoleId()
         .pipe(take(1))
         .subscribe((roleId) => {
            if (roleId === 9) {
               this.tprNecService.printNecReport(this.data.selectedToolId, data.necLec, false, this.data.selectedTprToolId, fixtureNo, this.data.programName, reportTag);
               this.refresh(data.necLec);
            }
            else {
               this.confirmation.showConfirmationDialog({
                  title: 'Save  File to NEC Folder',
                  cancelButton: true,
                  message: actionString,
                  disableClose: false,
                  onYesClick: () => {
                     if (!this.resourced) {
                        this.tprNecService.printNecReport(
                           this.data.selectedToolId,
                           data.necLec,
                           true,
                           this.data.selectedTprToolId,
                           this.data.fixtureNo,
                           this.data.programName,
                           reportTag);
                        this.refresh(data.necLec);
                        this.confirmation.closeConfirmationDialog();
                     }
                  },
                  onNoClick: () => {
                     this.tprNecService.printNecReport(this.data.selectedToolId, data.necLec, false, this.data.selectedTprToolId, fixtureNo, this.data.programName, reportTag);
                     this.refresh(data.necLec);
                     this.confirmation.closeConfirmationDialog();
                  }
               }).afterClosed().subscribe(() => this.refresh(data.necLec));
            }
         });

   }

   /**
    * Prints the NEC Report to a file
    *
    * @memberof TprNecModalComponent
    */
   onReportclick() {
      const selected = this.grid.selected;

      if (!selected) {
         this.toastr.error('Please select a row to print NEC report for.');
         return;
      }

      this.printReport(selected, this.data.fixtureNo, 'Do you want to save this file to the NEC folder?');
   }

   refresh(necLec?: any): void {
      this.refresh$.emit(necLec);
   }

   necLecSelected(selected: any) {
      this.selectedNecLec = selected;
   }

   /**
    * Cancel current dialog and close it
    *
    * @memberof TprNecModalComponent
    */
   cancel(): void {
      this.dialog.close();
   }

}
